import Quill from 'quill';
import Delta from 'quill-delta';
import DOMPurify from 'dompurify';

// Extended font family mapping for external content compatibility
const fontFamilyMap: Record<string, string> = {
  monospace: 'monospace',
  'courier new': 'monospace',
  courier: 'monospace',
  montserrat: 'montserrat',
  roboto: 'roboto',
  arial: 'montserrat', // Map Arial to Montserrat
  helvetica: 'montserrat', // Map Helvetica to Montserrat
  'helvetica neue': 'montserrat',
  verdana: 'montserrat',
  tahoma: 'montserrat',
  'trebuchet ms': 'montserrat',
  serif: 'serif',
  'times new roman': 'serif',
  times: 'serif',
  georgia: 'serif',
  'sans-serif': 'montserrat',
};

const fontSizeMap: Record<string, string> = {
  '6px': '6px',
  '8px': '8px',
  '10px': '10px',
  '12px': '12px',
  '15px': '15px',
  '16px': '15px', // Map 16px to 15px for compatibility
  '18px': '18px',
  '24px': '24px',
};

function extractFontClassFromStyle(
  style: CSSStyleDeclaration
): string | undefined {
  const family = style.fontFamily
    ?.toLowerCase()
    .split(',')[0]
    ?.replace(/['"]/g, '')
    .trim();
  return fontFamilyMap[family] ? fontFamilyMap[family] : undefined;
}

function extractFontSizeFromStyle(
  style: CSSStyleDeclaration
): string | undefined {
  const size = style.fontSize?.toLowerCase();
  return fontSizeMap[size] ? fontSizeMap[size] : undefined;
}

// Check if a font weight value indicates bold
function isBoldWeight(weight: string): boolean {
  const numWeight = parseInt(weight, 10);
  return (
    weight === 'bold' || weight === 'bolder' || (numWeight >= 600 && !isNaN(numWeight))
  );
}

export function htmlToQuillDelta(html: string): Delta {
  // Sanitize HTML to prevent XSS
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'div', 'span', 'br',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'ul', 'ol', 'li',
      'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    ],
    ALLOWED_ATTR: ['style', 'class', 'href', 'data-list'],
  });

  const container = document.createElement('div');
  container.innerHTML = sanitizedHtml;

  const delta = new Delta();

  const processNode = (
    node: Node,
    inheritedAttrs: any = {},
    listType?: 'bullet' | 'ordered'
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      // Only insert if there's actual content (not just whitespace between elements)
      if (text.trim() || text.includes(' ')) {
        return delta.insert(text, inheritedAttrs);
      }
      return delta;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return delta;

    const el = node as HTMLElement;
    const computedStyle = window.getComputedStyle(el);

    let attributes: any = { ...inheritedAttrs };

    // Strong / Bold - check tag and computed style
    if (
      el.tagName === 'STRONG' ||
      el.tagName === 'B' ||
      isBoldWeight(computedStyle.fontWeight) ||
      isBoldWeight(el.style.fontWeight)
    ) {
      attributes.bold = true;
    }

    // Em / Italic - check tag and computed style
    if (
      el.tagName === 'EM' ||
      el.tagName === 'I' ||
      computedStyle.fontStyle === 'italic' ||
      el.style.fontStyle === 'italic'
    ) {
      attributes.italic = true;
    }

    // Underline - check tag and computed/inline style
    if (
      el.tagName === 'U' ||
      computedStyle.textDecoration.includes('underline') ||
      el.style.textDecoration?.includes('underline')
    ) {
      attributes.underline = true;
    }

    // Strikethrough
    if (
      el.tagName === 'S' ||
      el.tagName === 'STRIKE' ||
      computedStyle.textDecoration.includes('line-through') ||
      el.style.textDecoration?.includes('line-through')
    ) {
      attributes.strike = true;
    }

    // Font family - check inline style first, then computed
    const inlineFont = extractFontClassFromStyle(el.style);
    const computedFont = extractFontClassFromStyle(computedStyle);
    if (inlineFont) {
      attributes.font = inlineFont;
    } else if (computedFont && !inheritedAttrs.font) {
      attributes.font = computedFont;
    }

    // Font size - only set if explicitly specified in inline style
    const size = extractFontSizeFromStyle(el.style);
    if (size) {
      attributes.size = size;
    }

    // Color - check inline style
    if (el.style.color) {
      attributes.color = el.style.color;
    }

    // Background - check inline style
    if (el.style.backgroundColor && el.style.backgroundColor !== 'transparent') {
      attributes.background = el.style.backgroundColor;
    }

    // Handle headers
    if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(el.tagName)) {
      attributes.bold = true;
      // Map header sizes
      const headerSizeMap: Record<string, string> = {
        H1: '24px',
        H2: '18px',
        H3: '15px',
        H4: '12px',
        H5: '10px',
        H6: '8px',
      };
      attributes.size = headerSizeMap[el.tagName] || '12px';

      Array.from(el.childNodes).forEach((child) => {
        processNode(child, attributes);
      });
      delta.insert('\n', {});
      return delta;
    }

    // Handle line break
    if (el.tagName === 'BR') {
      // Skip <br> that is the sole meaningful child of a block element (e.g. <p><br></p>).
      // The parent block already inserts a newline, so this avoids duplicating empty lines.
      const parent = el.parentElement;
      if (parent && (parent.tagName === 'P' || parent.tagName === 'DIV')) {
        const meaningfulChildren = Array.from(parent.childNodes).filter(
          (child) =>
            !(child.nodeType === Node.TEXT_NODE && !child.textContent?.trim())
        );
        if (meaningfulChildren.length === 1 && meaningfulChildren[0] === el) {
          return delta;
        }
      }
      delta.insert('\n', {});
      return delta;
    }

    // Handle lists
    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const fallbackListType = el.tagName === 'UL' ? 'bullet' : 'ordered';

      Array.from(el.children).forEach((child) => {
        if (child.tagName === 'LI') {
          // Use child's data-list if available, otherwise fallback
          const childListType =
            child.getAttribute('data-list') || fallbackListType;

          // Process list item children to preserve inline formatting
          const listItemAttrs = { ...attributes };
          Array.from(child.childNodes).forEach((liChild) => {
            processNode(liChild, listItemAttrs);
          });
          delta.insert('\n', { list: childListType });
        } else {
          processNode(child, attributes);
        }
      });

      return delta;
    }

    // List item (standalone, not within UL/OL context)
    if (el.tagName === 'LI') {
      Array.from(el.childNodes).forEach((child) => {
        processNode(child, attributes);
      });
      delta.insert('\n', { ...attributes, list: listType });
      return delta;
    }

    // Paragraphs and block-level
    if (el.tagName === 'P' || el.tagName === 'DIV') {
      Array.from(el.childNodes).forEach((child) => {
        processNode(child, attributes);
      });
      delta.insert('\n', {});
      return delta;
    }

    // Links
    if (el.tagName === 'A') {
      const href = el.getAttribute('href');
      if (href) {
        attributes.link = href;
      }
    }

    // Inline or other (SPAN, A, etc.)
    Array.from(el.childNodes).forEach((child) => {
      processNode(child, attributes);
    });

    return delta;
  };

  Array.from(container.childNodes).forEach((node) => {
    processNode(node);
  });

  return delta;
}
